/**
 * Rax Onboarding Component
 *
 * Beautiful onboarding flow for new users.
 * Steps: Welcome → Mode Selection → API Key (if BYOK) → Create Agent (with persona) → Connect Skills → Done
 *
 * PRD Requirements:
 * 1. Sign up -> Create first agent -> Connect skills -> Chat -> Expand
 * 2. Conversational prompts ("Want me to connect your calendar?")
 * 3. Progressive disclosure
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme, raxButtonStyles, raxInputStyles } from "../styles/rax-theme.js";

export type OnboardingMode = "managed" | "byok";
export type PersonaType = "researcher" | "designer" | "coder" | "writer" | "assistant";

export interface OnboardingData {
  mode: OnboardingMode;
  apiKey?: string;
  apiProvider?: "anthropic" | "openai" | "openrouter" | "google";
  firstAgentName?: string;
  firstAgentType?: "work" | "personal" | "custom";
  firstAgentPersona?: PersonaType;
  connectedSkills?: string[];
}

interface PersonaTemplate {
  id: PersonaType;
  name: string;
  description: string;
  icon: string;
  defaultSkills: string[];
  color: string;
  conversationalPrompt: string;
}

interface SkillConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  conversationalPrompt: string;
  category: "communication" | "productivity" | "development";
}

const PERSONA_TEMPLATES: PersonaTemplate[] = [
  {
    id: "researcher",
    name: "Researcher",
    description: "Expert at finding information, analyzing data, and summarizing findings",
    icon: "🔬",
    defaultSkills: ["web_search", "browser", "files"],
    color: "#4488CC",
    conversationalPrompt: "I can help you research anything! Want me to search the web?",
  },
  {
    id: "designer",
    name: "Designer",
    description: "Focused on visual tasks, UI/UX suggestions, and creative direction",
    icon: "🎨",
    defaultSkills: ["browser", "files", "code"],
    color: "#AA55CC",
    conversationalPrompt: "I can help with designs and visual work. Ready to create?",
  },
  {
    id: "coder",
    name: "Coder",
    description: "Expert at code generation, debugging, and software architecture",
    icon: "💻",
    defaultSkills: ["code", "terminal", "files", "web_search"],
    color: "#44AA66",
    conversationalPrompt:
      "I can write code, debug issues, and build features. What should we work on?",
  },
  {
    id: "writer",
    name: "Writer",
    description: "Focused on content creation, editing, and communication",
    icon: "✏️",
    defaultSkills: ["web_search", "gmail", "files"],
    color: "#FF8844",
    conversationalPrompt: "I can help you write and edit content. What are we working on?",
  },
  {
    id: "assistant",
    name: "Assistant",
    description: "General purpose helper for everyday tasks and coordination",
    icon: "🤖",
    defaultSkills: ["gmail", "calendar", "web_search"],
    color: "#CCAA33",
    conversationalPrompt: "I'm your personal assistant! Ready to help with anything.",
  },
];

const SKILL_CONFIGS: SkillConfig[] = [
  {
    id: "gmail",
    name: "Gmail",
    icon: "📧",
    description: "Read and send emails",
    conversationalPrompt: "Want me to connect to your Gmail? I can help manage your inbox.",
    category: "communication",
  },
  {
    id: "calendar",
    name: "Calendar",
    icon: "📅",
    description: "View and manage your schedule",
    conversationalPrompt: "Should I connect your calendar? I can help with scheduling.",
    category: "productivity",
  },
  {
    id: "slack",
    name: "Slack",
    icon: "💬",
    description: "Message your team",
    conversationalPrompt:
      "Want me to join your Slack workspace? I can help with team communication.",
    category: "communication",
  },
  {
    id: "notion",
    name: "Notion",
    icon: "📝",
    description: "Access your notes and docs",
    conversationalPrompt: "Should I connect to Notion? I can help manage your docs.",
    category: "productivity",
  },
  {
    id: "code",
    name: "Code",
    icon: "💻",
    description: "Write and run code",
    conversationalPrompt: "I can write and execute code. Ready to build something?",
    category: "development",
  },
  {
    id: "browser",
    name: "Browser",
    icon: "🌐",
    description: "Browse websites",
    conversationalPrompt: "I can browse the web for you. Need help finding something?",
    category: "development",
  },
];

@customElement("rax-onboarding")
export class RaxOnboarding extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    raxInputStyles,
    css`
      :host {
        display: block;
        background: var(--bg-primary);
        min-height: 100vh;
      }
      
      .onboarding {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: var(--space-6);
      }
      
      .onboarding-card {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-8);
        max-width: 560px;
        width: 100%;
        text-align: center;
      }
      
      .logo {
        font-size: 64px;
        margin-bottom: var(--space-4);
        display: block;
      }
      
      .title {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .title .accent {
        color: var(--rax-yellow);
        text-shadow: 0 0 30px var(--accent-glow);
      }
      
      .subtitle {
        font-size: var(--font-md);
        color: var(--text-secondary);
        margin-bottom: var(--space-6);
        line-height: 1.6;
      }
      
      /* Step indicator */
      .steps {
        display: flex;
        justify-content: center;
        gap: var(--space-3);
        margin-bottom: var(--space-6);
      }
      
      .step-line {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .step-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-sm);
        font-weight: 600;
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        color: var(--text-tertiary);
        transition: all var(--transition-fast);
      }
      
      .step-circle.active {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
        box-shadow: 0 0 15px var(--accent-glow);
      }
      
      .step-circle.completed {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .step-connector {
        width: 40px;
        height: 2px;
        background: var(--border-color);
      }
      
      .step-connector.completed {
        background: var(--rax-yellow);
      }
      
      /* Mode selection */
      .mode-options {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        text-align: left;
      }
      
      .mode-option {
        padding: var(--space-5);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: all var(--transition-fast);
        background: var(--bg-elevated);
      }
      
      .mode-option:hover {
        border-color: var(--rax-yellow);
      }
      
      .mode-option.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
        box-shadow: 0 0 25px var(--accent-glow);
      }
      
      .mode-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-2);
      }
      
      .mode-icon {
        font-size: 28px;
      }
      
      .mode-name {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .mode-badge {
        font-size: var(--font-xs);
        padding: 2px 8px;
        background: var(--success);
        color: white;
        border-radius: var(--radius-full);
        font-weight: 500;
      }
      
      .mode-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.5;
        margin-bottom: var(--space-3);
      }
      
      .mode-features {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }
      
      .mode-feature {
        font-size: var(--font-xs);
        padding: var(--space-1) var(--space-2);
        background: var(--bg-hover);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
      }
      
      /* API Key input */
      .api-key-form {
        text-align: left;
      }
      
      .provider-tabs {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }
      
      .provider-tab {
        flex: 1;
        padding: var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: center;
      }
      
      .provider-tab:hover {
        border-color: var(--text-tertiary);
      }
      
      .provider-tab.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
      }
      
      .provider-tab .name {
        font-weight: 600;
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      
      .security-note {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-color);
        margin-top: var(--space-4);
        text-align: left;
      }
      
      .security-note .icon {
        font-size: 20px;
        flex-shrink: 0;
      }
      
      .security-note .text {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        line-height: 1.5;
      }
      
      /* Agent creation quick */
      .agent-types {
        display: flex;
        gap: var(--space-3);
        margin-bottom: var(--space-5);
      }
      
      .agent-type {
        flex: 1;
        padding: var(--space-4);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: all var(--transition-fast);
        background: var(--bg-elevated);
        text-align: center;
      }
      
      .agent-type:hover {
        border-color: var(--rax-yellow);
      }
      
      .agent-type.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
      }
      
      .agent-type .icon {
        font-size: 32px;
        margin-bottom: var(--space-2);
      }
      
      .agent-type .label {
        font-weight: 600;
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      
      /* Complete screen */
      .complete-icon {
        font-size: 80px;
        margin-bottom: var(--space-4);
        animation: bounce 1s ease infinite;
      }
      
      @keyframes bounce {
        0%,
        100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-10px);
        }
      }
      
      .complete-title {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
        margin-bottom: var(--space-2);
        text-shadow: 0 0 30px var(--accent-glow);
      }
      
      /* Actions */
      .actions {
        display: flex;
        gap: var(--space-3);
        margin-top: var(--space-6);
        justify-content: center;
      }
      
      .skip-link {
        color: var(--text-tertiary);
        font-size: var(--font-sm);
        margin-top: var(--space-4);
        cursor: pointer;
        transition: color var(--transition-fast);
      }
      
      .skip-link:hover {
        color: var(--text-secondary);
      }
      
      /* Form groups */
      .form-group {
        margin-bottom: var(--space-4);
        text-align: left;
      }
      
      /* Persona selection */
      .persona-grid {
        display: grid;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
        text-align: left;
      }
      
      .persona-card {
        border: 2px solid var(--border-color);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .persona-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .persona-card.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
        box-shadow: 0 0 20px var(--accent-glow);
      }
      
      .persona-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-2);
      }
      
      .persona-icon {
        font-size: 24px;
      }
      
      .persona-name {
        font-weight: 600;
        color: var(--text-primary);
        flex: 1;
      }
      
      .persona-color {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
      
      .persona-description {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        line-height: 1.4;
      }
      
      /* Skill connection */
      .skill-section {
        text-align: left;
      }
      
      .conversational-prompt {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
        margin-bottom: var(--space-4);
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
      }
      
      .prompt-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--rax-yellow);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        flex-shrink: 0;
      }
      
      .prompt-content {
        flex: 1;
      }
      
      .prompt-message {
        font-size: var(--font-sm);
        color: var(--text-primary);
        line-height: 1.5;
        margin-bottom: var(--space-3);
      }
      
      .prompt-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .prompt-btn {
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
      }
      
      .prompt-btn.primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .prompt-btn.primary:hover {
        background: var(--rax-gold);
      }
      
      .prompt-btn.secondary {
        background: var(--bg-hover);
        color: var(--text-secondary);
      }
      
      .prompt-btn.secondary:hover {
        background: var(--border-color);
      }
      
      .skills-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }
      
      .skill-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        transition: all var(--transition-fast);
      }
      
      .skill-item:hover {
        border-color: var(--text-tertiary);
      }
      
      .skill-item.connected {
        border-color: var(--success);
        background: rgba(74, 222, 128, 0.05);
      }
      
      .skill-icon {
        font-size: 24px;
        flex-shrink: 0;
      }
      
      .skill-details {
        flex: 1;
        min-width: 0;
      }
      
      .skill-name {
        font-weight: 600;
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      
      .skill-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .skill-status {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-size: var(--font-xs);
        color: var(--success);
      }
      
      .skill-action {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-hover);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .skill-action:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .category-title {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: var(--space-4) 0 var(--space-2);
        font-weight: 500;
      }
      
      .category-title:first-child {
        margin-top: 0;
      }
      
      /* Progress indicators for skills step */
      .skill-progress {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
      }
      
      .progress-text {
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      .progress-bar-container {
        width: 120px;
        height: 6px;
        background: var(--bg-hover);
        border-radius: var(--radius-full);
        overflow: hidden;
      }
      
      .progress-bar-fill {
        height: 100%;
        background: var(--rax-yellow);
        transition: width var(--transition-base);
      }
    `,
  ];

  @property({ type: Number })
  currentStep = 1;

  @state()
  private mode: OnboardingMode | null = null;

  @state()
  private apiProvider: "anthropic" | "openai" | "google" | "openrouter" = "anthropic";

  @state()
  private apiKey = "";

  @state()
  private agentType: "work" | "personal" | "custom" | null = "work";

  @state()
  private agentName = "";

  @state()
  private selectedPersona: PersonaType | null = null;

  @state()
  private connectedSkills: string[] = [];

  @state()
  private currentSkillPromptIndex = 0;

  @state()
  private showingSkillPrompt = true;

  // Total steps: 1=Welcome, 2=Mode, 3=API Key (BYOK only), 4=Agent+Persona, 5=Skills, 6=Complete
  private get totalSteps(): number {
    return this.mode === "byok" ? 6 : 5;
  }

  // Get skills to suggest based on persona
  private get suggestedSkills(): SkillConfig[] {
    if (this.selectedPersona) {
      const persona = PERSONA_TEMPLATES.find((p) => p.id === this.selectedPersona);
      if (persona) {
        return SKILL_CONFIGS.filter((s) => persona.defaultSkills.includes(s.id));
      }
    }
    // Default suggestions for no persona
    return SKILL_CONFIGS.filter((s) => ["gmail", "calendar", "slack"].includes(s.id));
  }

  private get currentSkillPrompt(): SkillConfig | null {
    const availableSkills = this.suggestedSkills.filter(
      (s) => !this.connectedSkills.includes(s.id),
    );
    return availableSkills[0] || null;
  }

  override render() {
    // Calculate which logical step we're on for display
    const displayStep = this.getDisplayStep();

    return html`
      <div class="onboarding">
        <div class="onboarding-card">
          ${this.renderStepIndicator()}
          ${this.currentStep === 1 ? this.renderWelcome() : ""}
          ${this.currentStep === 2 ? this.renderModeSelection() : ""}
          ${this.currentStep === 3 && this.mode === "byok" ? this.renderApiKeyInput() : ""}
          ${this.currentStep === 3 && this.mode === "managed" ? this.renderAgentCreation() : ""}
          ${this.currentStep === 4 && this.mode === "byok" ? this.renderAgentCreation() : ""}
          ${this.currentStep === 4 && this.mode === "managed" ? this.renderSkillConnection() : ""}
          ${this.currentStep === 5 && this.mode === "byok" ? this.renderSkillConnection() : ""}
          ${this.currentStep === 5 && this.mode === "managed" ? this.renderComplete() : ""}
          ${this.currentStep === 6 ? this.renderComplete() : ""}
        </div>
      </div>
    `;
  }

  private getDisplayStep(): number {
    // Map internal step to display step (1-based, skipping API key for managed)
    if (this.mode === "managed") {
      // Managed: Welcome(1) -> Mode(2) -> Agent(3) -> Skills(4) -> Complete(5)
      if (this.currentStep <= 2) return this.currentStep;
      return this.currentStep; // Steps 3+ map directly
    } else {
      // BYOK: Welcome(1) -> Mode(2) -> API Key(3) -> Agent(4) -> Skills(5) -> Complete(6)
      return this.currentStep;
    }
  }

  private renderStepIndicator() {
    // Hide indicator on welcome and final complete screens
    const isComplete =
      (this.mode === "managed" && this.currentStep === 5) ||
      (this.mode === "byok" && this.currentStep === 6);
    if (this.currentStep === 1 || isComplete) return "";

    // Calculate display steps based on mode
    // Managed: Mode(2) -> Agent(3) -> Skills(4) = 3 dots
    // BYOK: Mode(2) -> API Key(3) -> Agent(4) -> Skills(5) = 4 dots
    const totalDots = this.mode === "byok" ? 4 : 3;
    const displaySteps = Array.from({ length: totalDots }, (_, i) => i + 1);

    // Calculate which dot should be active (offset by 1 since we start at step 2)
    const activeStep = this.currentStep - 1;

    return html`
      <div class="steps">
        ${displaySteps.map(
          (step, i) => html`
          <div class="step-line">
            <div class="step-circle ${
              step < activeStep ? "completed" : step === activeStep ? "active" : ""
            }">
              ${step < activeStep ? "✓" : step}
            </div>
            ${
              i < displaySteps.length - 1
                ? html`
              <div class="step-connector ${step < activeStep ? "completed" : ""}"></div>
            `
                : ""
            }
          </div>
        `,
        )}
      </div>
    `;
  }

  private renderWelcome() {
    return html`
      <span class="logo">⚡</span>
      <h1 class="title">Welcome to <span class="accent">Rax</span></h1>
      <p class="subtitle">
        Your AI band is about to start jamming. Let's get you set up in under 2 minutes.
      </p>
      <div class="actions">
        <button class="btn btn-primary btn-lg" @click=${() => (this.currentStep = 2)}>
          ⚡ Let's Go
        </button>
      </div>
    `;
  }

  private renderModeSelection() {
    return html`
      <h1 class="title">How do you want to use <span class="accent">Rax</span>?</h1>
      <p class="subtitle">
        Bring your own API keys for full control over your AI agent.
      </p>

      <div class="mode-options">
        <div
          class="mode-option disabled"
          style="opacity: 0.5; cursor: not-allowed; pointer-events: none;"
        >
          <div class="mode-header">
            <span class="mode-icon">🚀</span>
            <span class="mode-name">Managed Mode</span>
            <span class="mode-badge" style="background: #555; color: #aaa;">Coming Soon</span>
          </div>
          <p class="mode-description">
            We'll handle the AI infrastructure. Just chat and pay for what you use. No setup required.
          </p>
          <div class="mode-features">
            <span class="mode-feature">⚡ Instant start</span>
            <span class="mode-feature">💰 Pay-per-use</span>
            <span class="mode-feature">🔄 Auto-scaling</span>
          </div>
        </div>

        <div
          class="mode-option ${this.mode === "byok" ? "selected" : ""}"
          @click=${() => (this.mode = "byok")}
        >
          <div class="mode-header">
            <span class="mode-icon">🔑</span>
            <span class="mode-name">Bring Your Own Key</span>
            <span class="mode-badge">Available Now</span>
          </div>
          <p class="mode-description">
            Use your own API keys from Anthropic, OpenAI, or OpenRouter. Full control over usage and billing.
          </p>
          <div class="mode-features">
            <span class="mode-feature">🎛️ Full control</span>
            <span class="mode-feature">💳 Direct billing</span>
            <span class="mode-feature">🔐 Your keys</span>
          </div>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${() => (this.currentStep = 1)}>
          Back
        </button>
        <button class="btn btn-primary" @click=${this.onModeNext} ?disabled=${!this.mode}>
          Continue
        </button>
      </div>
    `;
  }

  private renderApiKeyInput() {
    return html`
      <h1 class="title">Add your <span class="accent">API key</span></h1>
      <p class="subtitle">
        Your key is encrypted and never logged. We only use it to make API calls on your behalf.
      </p>

      <div class="api-key-form">
        <div class="provider-tabs">
          <div
            class="provider-tab ${this.apiProvider === "anthropic" ? "selected" : ""}"
            @click=${() => (this.apiProvider = "anthropic")}
          >
            <div class="name">Anthropic</div>
          </div>
          <div
            class="provider-tab ${this.apiProvider === "openai" ? "selected" : ""}"
            @click=${() => (this.apiProvider = "openai")}
          >
            <div class="name">OpenAI</div>
          </div>
          <div
            class="provider-tab ${this.apiProvider === "google" ? "selected" : ""}"
            @click=${() => (this.apiProvider = "google")}
          >
            <div class="name">Google</div>
          </div>
          <div
            class="provider-tab ${this.apiProvider === "openrouter" ? "selected" : ""}"
            @click=${() => (this.apiProvider = "openrouter")}
          >
            <div class="name">OpenRouter</div>
          </div>
        </div>

        <div class="form-group">
          <label class="input-label">API Key</label>
          <input
            class="input"
            type="password"
            placeholder="${this.getApiKeyPlaceholder()}"
            .value=${this.apiKey}
            @input=${(e: InputEvent) => (this.apiKey = (e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="security-note">
          <span class="icon">🔐</span>
          <span class="text">
            Your key is encrypted with AES-256-GCM and never appears in logs, prompts, or error messages.
            You can rotate or revoke it anytime.
          </span>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${() => (this.currentStep = 2)}>
          Back
        </button>
        <button class="btn btn-primary" @click=${this.onApiKeyNext} ?disabled=${!this.apiKey}>
          Continue
        </button>
      </div>
    `;
  }

  private renderAgentCreation() {
    return html`
      <h1 class="title">Create your first <span class="accent">agent</span></h1>
      <p class="subtitle">
        Choose a persona for your AI assistant. This helps tailor their skills and personality.
      </p>

      <div class="persona-grid">
        ${PERSONA_TEMPLATES.map(
          (persona) => html`
          <div
            class="persona-card ${this.selectedPersona === persona.id ? "selected" : ""}"
            @click=${() => this.selectPersona(persona)}
          >
            <div class="persona-header">
              <span class="persona-icon">${persona.icon}</span>
              <span class="persona-name">${persona.name}</span>
              <span class="persona-color" style="background: ${persona.color}"></span>
            </div>
            <div class="persona-description">${persona.description}</div>
          </div>
        `,
        )}
      </div>

      <div class="form-group">
        <label class="input-label">Agent Name</label>
        <input
          class="input"
          type="text"
          placeholder="${this.getAgentNamePlaceholder()}"
          .value=${this.agentName}
          @input=${(e: InputEvent) => (this.agentName = (e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this.onAgentBack}>
          Back
        </button>
        <button class="btn btn-primary" @click=${this.onAgentNext}>
          Continue
        </button>
      </div>

      <div class="skip-link" @click=${this.skipPersonaSelection}>
        Skip persona selection
      </div>
    `;
  }

  private selectPersona(persona: (typeof PERSONA_TEMPLATES)[0]) {
    this.selectedPersona = persona.id;
    // Pre-fill agent name based on persona
    if (!this.agentName) {
      this.agentName = persona.name;
    }
  }

  private skipPersonaSelection() {
    this.selectedPersona = null;
    this.onAgentNext();
  }

  private renderSkillConnection() {
    const connectedCount = this.connectedSkills.length;
    const totalSuggested = this.suggestedSkills.length;
    const progress = totalSuggested > 0 ? (connectedCount / totalSuggested) * 100 : 0;

    // Get the current skill to prompt about
    const currentPrompt = this.currentSkillPrompt;

    // Group skills by category
    const communicationSkills = SKILL_CONFIGS.filter((s) => s.category === "communication");
    const productivitySkills = SKILL_CONFIGS.filter((s) => s.category === "productivity");
    const developmentSkills = SKILL_CONFIGS.filter((s) => s.category === "development");

    return html`
      <h1 class="title">Connect your <span class="accent">tools</span></h1>
      <p class="subtitle">
        Give your agent access to the tools they need. You can always add more later.
      </p>

      <div class="skill-section">
        ${
          this.showingSkillPrompt && currentPrompt
            ? html`
          <div class="conversational-prompt">
            <div class="prompt-avatar">
              ${this.selectedPersona ? PERSONA_TEMPLATES.find((p) => p.id === this.selectedPersona)?.icon || "⚡" : "⚡"}
            </div>
            <div class="prompt-content">
              <div class="prompt-message">
                ${currentPrompt.conversationalPrompt}
              </div>
              <div class="prompt-actions">
                <button class="prompt-btn primary" @click=${() => this.connectSkill(currentPrompt.id)}>
                  Yes, connect ${currentPrompt.name}
                </button>
                <button class="prompt-btn secondary" @click=${() => this.skipSkillPrompt()}>
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        `
            : ""
        }

        <div class="skill-progress">
          <span class="progress-text">${connectedCount} connected</span>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" style="width: ${progress}%"></div>
          </div>
        </div>

        <div class="category-title">Communication</div>
        <div class="skills-list">
          ${communicationSkills.map((skill) => this.renderSkillItem(skill))}
        </div>

        <div class="category-title">Productivity</div>
        <div class="skills-list">
          ${productivitySkills.map((skill) => this.renderSkillItem(skill))}
        </div>

        <div class="category-title">Development</div>
        <div class="skills-list">
          ${developmentSkills.map((skill) => this.renderSkillItem(skill))}
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this.onSkillsBack}>
          Back
        </button>
        <button class="btn btn-primary" @click=${this.onComplete}>
          ⚡ Start Chatting
        </button>
      </div>

      <div class="skip-link" @click=${this.onComplete}>
        Skip and connect later
      </div>
    `;
  }

  private renderSkillItem(skill: SkillConfig) {
    const isConnected = this.connectedSkills.includes(skill.id);

    return html`
      <div class="skill-item ${isConnected ? "connected" : ""}">
        <span class="skill-icon">${skill.icon}</span>
        <div class="skill-details">
          <div class="skill-name">${skill.name}</div>
          <div class="skill-desc">${skill.description}</div>
        </div>
        ${
          isConnected
            ? html`
                <div class="skill-status"><span>✓</span> Connected</div>
              `
            : html`
          <button class="skill-action" @click=${() => this.connectSkill(skill.id)}>
            Connect
          </button>
        `
        }
      </div>
    `;
  }

  private connectSkill(skillId: string) {
    if (!this.connectedSkills.includes(skillId)) {
      this.connectedSkills = [...this.connectedSkills, skillId];
    }
    // Move to next prompt
    this.currentSkillPromptIndex++;
    this.showingSkillPrompt = true;
  }

  private skipSkillPrompt() {
    this.currentSkillPromptIndex++;
    // Check if we have more prompts
    const availableSkills = this.suggestedSkills.filter(
      (s) => !this.connectedSkills.includes(s.id),
    );
    this.showingSkillPrompt = availableSkills.length > 0;
  }

  private onAgentNext() {
    // Move to skills step
    if (this.mode === "byok") {
      this.currentStep = 5;
    } else {
      this.currentStep = 4;
    }
    // Reset skill prompts
    this.currentSkillPromptIndex = 0;
    this.showingSkillPrompt = true;
  }

  private onSkillsBack() {
    // Go back to agent creation
    if (this.mode === "byok") {
      this.currentStep = 4;
    } else {
      this.currentStep = 3;
    }
  }

  private renderComplete() {
    const personaIcon = this.selectedPersona
      ? PERSONA_TEMPLATES.find((p) => p.id === this.selectedPersona)?.icon || "⚡"
      : "⚡";
    const agentDisplayName = this.agentName || this.getAgentNamePlaceholder();

    return html`
      <div class="complete-icon">${personaIcon}</div>
      <h1 class="complete-title">${agentDisplayName} is ready!</h1>
      <p class="subtitle">
        ${
          this.connectedSkills.length > 0
            ? `Connected ${this.connectedSkills.length} skill${this.connectedSkills.length > 1 ? "s" : ""}. You can add more anytime from the dashboard.`
            : "Start chatting with your agent. You can connect skills later from the dashboard."
        }
      </p>
      <div class="actions">
        <button class="btn btn-primary btn-lg" @click=${this.onStartChatting}>
          ⚡ Start Chatting
        </button>
      </div>
    `;
  }

  private getApiKeyPlaceholder(): string {
    switch (this.apiProvider) {
      case "anthropic":
        return "sk-ant-...";
      case "openai":
        return "sk-...";
      case "google":
        return "AIzaSy...";
      case "openrouter":
        return "sk-or-...";
    }
  }

  private getAgentNamePlaceholder(): string {
    // First check persona
    if (this.selectedPersona) {
      const persona = PERSONA_TEMPLATES.find((p) => p.id === this.selectedPersona);
      if (persona) return persona.name;
    }
    // Fall back to agent type
    switch (this.agentType) {
      case "work":
        return "Work Assistant";
      case "personal":
        return "Personal Helper";
      default:
        return "My Agent";
    }
  }

  private onModeNext() {
    if (this.mode === "byok") {
      this.currentStep = 3; // API key entry
    } else {
      // Managed mode - skip API key, go to agent creation
      this.currentStep = 3; // Agent creation (step 3 in managed flow)
    }
  }

  private onApiKeyNext() {
    this.currentStep = 4; // Agent creation for BYOK
  }

  private onAgentBack() {
    if (this.mode === "byok") {
      this.currentStep = 3; // Back to API key
    } else {
      this.currentStep = 2; // Back to mode selection
    }
  }

  private onComplete() {
    const data: OnboardingData = {
      mode: this.mode!,
      apiKey: this.mode === "byok" ? this.apiKey : undefined,
      apiProvider: this.mode === "byok" ? this.apiProvider : undefined,
      firstAgentName: this.agentName || this.getAgentNamePlaceholder(),
      firstAgentType: this.agentType || "work",
      firstAgentPersona: this.selectedPersona || undefined,
      connectedSkills: this.connectedSkills.length > 0 ? this.connectedSkills : undefined,
    };

    this.dispatchEvent(
      new CustomEvent("onboarding-complete", {
        bubbles: true,
        detail: data,
      }),
    );
  }

  private onStartChatting() {
    this.dispatchEvent(new CustomEvent("start-chatting", { bubbles: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-onboarding": RaxOnboarding;
  }
}
